import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SystemStatus } from '../../components/SystemStatus';

function LogoUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [currentLogo, setCurrentLogo] = useState('/api/assets/branding/logo.png');
  const [uploadStatus, setUploadStatus] = useState('');

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(png|jpg|jpeg|svg\+xml)$/)) {
      setUploadStatus('Error: Only PNG, JPG, JPEG, and SVG files are allowed');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadStatus('Error: File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setUploadStatus('Uploading...');

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        const response = await fetch('/api/v1/admin/upload-logo', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            logo_data: base64Data,
            filename: file.name
          })
        });

        const result = await response.json();

        if (result.success) {
          setUploadStatus('Logo updated successfully!');
          // Force reload the logo by adding a timestamp
          setCurrentLogo(`${result.logo_url}?t=${Date.now()}`);
          // Reload the page after a delay to show the new logo in header
          setTimeout(() => window.location.reload(), 1000);
        } else {
          setUploadStatus(`Error: ${result.error}`);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadStatus(`Error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="p-6 bg-background rounded-lg">
      <h3 className="text-lg font-semibold text-foreground mb-4">Logo Management</h3>
      
      {/* Current Logo Preview */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-card-foreground mb-2">Current Logo</label>
        <div className="flex items-center space-x-4">
          <img
            src={currentLogo}
            alt="Current logo"
            className="h-12 w-auto border border-border rounded bg-card p-2"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <span className="text-sm text-muted-foreground">Logo appears in the site header</span>
        </div>
      </div>

      {/* Upload Section */}
      <div>
        <label className="block text-sm font-medium text-card-foreground mb-2">
          Upload New Logo
        </label>
        <div className="flex items-center space-x-4">
          <input
            type="file"
            accept=".png,.jpg,.jpeg,.svg"
            onChange={handleFileUpload}
            disabled={isUploading}
            className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-accent file:text-blue-700 hover:file:bg-blue-100"
          />
          {isUploading && <span className="text-blue-600">Uploading...</span>}
        </div>
        
        {uploadStatus && (
          <div className={`mt-2 text-sm ${uploadStatus.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
            {uploadStatus}
          </div>
        )}
        
        <p className="mt-2 text-xs text-muted-foreground">
          Supported formats: PNG, JPG, JPEG, SVG. Max size: 5MB. Logo persists across deployments.
        </p>
      </div>
    </div>
  );
}

const AdminSystemPage = () => {
  return (
    <div className="space-y-8">
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">System Settings</h1>
              <p className="text-sm text-muted-foreground mt-1">System health, branding, and configuration</p>
            </div>
            <Link to="/admin" className="btn btn-secondary">
              ← Back to Admin
            </Link>
          </div>
        </div>
        
        <div className="card-body space-y-8">
          {/* Logo Management */}
          <LogoUpload />
          
          {/* System Status */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-4">System Status</h2>
            <SystemStatus />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSystemPage;